import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage, 
  Divider, 
  DividerText 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';
import { LoginRequest } from '../types/auth';

/**
 * Base62 扩展字符映射：
 * + → zA
 * / → zB
 * z → zz
 */

const Base62 = {
  // Base64 → Base62
  encode(b64: string) {
    let out = '';
    for (const ch of b64) {
      if (ch === '+') out += 'zA';
      else if (ch === '/') out += 'zB';
      else if (ch === 'z') out += 'zz';
      else out += ch;
    }
    return out;
  },

  // Base62 → Base64（更稳健）
  decode(s: string) {
    let b64 = '';
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === 'z') {
        // 检查是否越界
        if (i + 1 >= s.length) {
          throw new Error("Invalid Base62: trailing 'z' with no escape char");
        }
        const nxt = s[++i];
        if (nxt === 'A') b64 += '+';
        else if (nxt === 'B') b64 += '/';
        else if (nxt === 'z') b64 += 'z';
        else throw new Error("Invalid escape after z: " + nxt);
      } else {
        b64 += ch;
      }
    }
    return b64;
  }
};

/**
 * UTF-8 ↔ Base64（浏览器兼容）
 */

function encodeUTF8ToBase64(str: string) {
    const bytes = new TextEncoder().encode(str);
    let binary = "";
    // @ts-ignore
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary);
}

function decodeBase64ToUTF8(b64: string) {
    // 填 padding
    const mod = b64.length % 4;
    if (mod) b64 += "=".repeat(4 - mod);

    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
}

/**
 * 自动识别 Base62 / Base64
 */
// @ts-ignore
export function smartDecode(input) {
    const hasZ = input.includes("z");

    try {
        let b64 = hasZ ? Base62.decode(input) : input;
        return decodeBase64ToUTF8(b64);
    } catch (err) {
        console.error(err);
        return "/";
    }
}

/**
 * 一条龙 encode：UTF-8 → Base64 → Base62
 */
export function encodeToBase62(str: string) {
    const b64 = encodeUTF8ToBase64(str);
    return Base62.encode(b64);
}


const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  transition: color ${theme.transitions.fast}, background-color ${theme.transitions.fast}, transform ${theme.transitions.fast};
  
  &:hover {
    color: ${theme.colors.primaryLight};
    background-color: rgba(255, 255, 255, 0.08);
    transform: translateY(-50%) translateY(-1px);
  }
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary};
`;

const CheckboxLabel = styled.label`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
`;

const ForgotPasswordLink = styled(Link)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignUpPrompt = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const SignUpLink = styled(Link)`
  color: ${theme.colors.primary};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const validationSchema = yup.object({
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

export const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect path from URL params or state
  const urlParams = new URLSearchParams(location.search);
  const redirectParam = urlParams.get('redirect');
  
  let redirectPath = '/';
  if (redirectParam) {
    // Decode the base62 encoded path
    redirectPath = decodeBase64ToUTF8(redirectParam);
  } else if ((location.state as any)?.from?.pathname) {
    // Fallback to router state
    redirectPath = (location.state as any).from.pathname;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginRequest>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: LoginRequest) => {
    setIsLoading(true);
    
    try {
      const response = await authService.login(data);
      
      if (response.success) {
        toast.success('Login successful!');
        // Redirect to the page they were trying to visit or home
        // navigate(redirectPath, { replace: true });
        window.location.href = redirectPath;
      } else {
        if (response.error?.includes('username')) {
          setError('username', { message: response.error });
        } else if (response.error?.includes('password')) {
          setError('password', { message: response.error });
        } else {
          toast.error(response.error || 'Login failed');
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout
      title="Welcome Back"
      subtitle="Sign in to your ZJULAB AI account"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="username">Username</Label>
          <InputContainer>
            <InputIcon>
              <User size={20} />
            </InputIcon>
            <StyledInput
              id="username"
              type="text"
              placeholder="Enter your username"
              $hasError={!!errors.username}
              {...register('username')}
            />
          </InputContainer>
          {errors.username && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.username.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              $hasError={!!errors.password}
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.password && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.password.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <RememberMeContainer>
          <CheckboxContainer>
            <Checkbox type="checkbox" id="remember" />
            <CheckboxLabel htmlFor="remember">Remember me</CheckboxLabel>
          </CheckboxContainer>
          <ForgotPasswordLink to="/forgot-password">
            Forgot password?
          </ForgotPasswordLink>
        </RememberMeContainer>

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </Button>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <SignUpPrompt>
          Don't have an account?{' '}
          <SignUpLink to="/register">Sign up for free</SignUpLink>
        </SignUpPrompt>
      </Form>
    </Layout>
  );
};
